import React, { useEffect, useState, useRef } from "react";
import API from "../api/axiosConfig";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Star, Building2, Share2, Heart, ArrowLeft,
  MapPin, ChevronDown, ShoppingCart, BadgeCheck, ImageOff, Plus, Minus,
  ChevronLeft, ChevronRight, X, ZoomIn,
} from "lucide-react";
import MobileBottomBar from "../components/MobileBottomBar";
import { useCart } from "../context/CartContext";
import { toast } from "react-toastify";
import CartToast from "../components/CartToast";
import { useFavorites } from "../context/FavoritesContext";
import { isSedeOpen } from "../utils/sedeStatus";
import { getImageUrl } from "../utils/getImageUrl";
import { shareContent } from "../utils/share";

function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function StarRow({ value, size = 14 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size}
          className={n <= value ? "text-yellow-400 fill-yellow-400" : "text-gray-200 fill-gray-200"} />
      ))}
    </div>
  );
}

function StarPicker({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={24} onClick={() => onChange(n)}
          className={`cursor-pointer transition ${n <= value ? "text-yellow-400 fill-yellow-400" : "text-gray-300"}`} />
      ))}
    </div>
  );
}

function QtySelector({ qty, setQty }) {
  return (
    <div className="flex items-center gap-1 border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setQty(q => Math.max(1, q - 1))}
        className="w-11 h-11 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition">
        <Minus size={16} />
      </button>
      <span className="w-8 text-center text-sm font-bold text-gray-800">{qty}</span>
      <button
        onClick={() => setQty(q => q + 1)}
        className="w-11 h-11 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition">
        <Plus size={16} />
      </button>
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct]           = useState(null);
  const [imgIndex, setImgIndex]         = useState(0);
  const [zoomOpen, setZoomOpen]         = useState(false);
  const [sedesOrdenadas, setSedesOrdenadas] = useState([]);
  const [reviews, setReviews]           = useState([]);
  const [rating, setRating]             = useState(0);
  const [comment, setComment]           = useState("");
  const [alreadyReviewed, setAlreadyReviewed] = useState(false);
  const [hasPurchased,    setHasPurchased]    = useState(false);
  const [showReviews, setShowReviews]   = useState(false);
  const [qty, setQty]                   = useState(1);
  const touchStartX = useRef(null);

  const { addToCart }    = useCart();
  const { favorites, toggleFavorite } = useFavorites();
  const isLoggedIn = !!localStorage.getItem("token");

  const isFav  = product && favorites.some((f) => f.id === product.id);
  const canBuy = sedesOrdenadas.length === 0 || sedesOrdenadas.some((s) => isSedeOpen(s).open);

  /* ── Data fetching ── */
  useEffect(() => {
    API.post(`/metrics/product-view/${id}`).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!isLoggedIn) return;
    API.get(`/reviews/product/${id}/user/check`)
      .then((res) => {
        const d = res.data?.data ?? res.data;
        setAlreadyReviewed(d?.reviewed ?? false);
        setHasPurchased(d?.hasPurchased ?? false);
      })
      .catch(() => {});
  }, [id, isLoggedIn]);

  useEffect(() => {
    API.get(`/reviews/product/${id}`)
      .then((res) => { const d = res.data?.data ?? res.data; setReviews(d?.reviews ?? d ?? []); })
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    API.get(`/products/${id}`)
      .then((res) => {
        const data = res.data?.data ?? res.data;
        const prod = data?.product ?? data;
        setProduct(prod);
        setImgIndex(0);

        const rawSedes = Array.isArray(prod.sedes) && prod.sedes.length > 0
          ? prod.sedes
          : Array.isArray(prod.productSedes)
            ? prod.productSedes.map((ps) => ps.sede ?? ps)
            : [];

        if (rawSedes.length === 0) { setSedesOrdenadas([]); return; }

        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const { latitude: lat, longitude: lng } = pos.coords;
            setSedesOrdenadas(
              rawSedes
                .map((s) => {
                  const sLat = Number(s.lat ?? s.location?.lat);
                  const sLng = Number(s.lng ?? s.location?.lng);
                  return { ...s, distance: sLat && sLng ? haversine(lat, lng, sLat, sLng) : Infinity };
                })
                .sort((a, b) => a.distance - b.distance)
            );
          },
          () => setSedesOrdenadas(rawSedes)
        );
      })
      .catch(() => {});
  }, [id]);

  /* ── Handlers ── */
  const handleFavorite = () => {
    if (!isLoggedIn) { toast.info("Inicia sesión para guardar favoritos"); navigate("/login"); return; }
    toggleFavorite(product);
    toast(isFav ? "Quitado de favoritos" : "Guardado en favoritos", { autoClose: 1500 });
  };

  const handleShare = async () => {
    const url = `${(process.env.REACT_APP_URL || "http://localhost:3000").replace(/\/$/, "")}/share/product/${product.id}`;
    try {
      const result = await shareContent({
        title: product.title,
        text: `Mira ${product.title} en Qkiere`,
        url,
        imageUrl: product.images?.[0] ? getImageUrl(product.images[0]) : null,
        fileName: "producto.jpg",
      });
      if (result === "copied") toast.info("Enlace copiado");
    } catch {}
  };

  const handleAddToCart = () => {
    if (!canBuy) { toast.info("Todas las sedes están cerradas"); return; }
    for (let i = 0; i < qty; i++) addToCart(product);
    toast(<CartToast product={product} />, { icon: "🛒", autoClose: 2000 });
  };

  const handleSubmitReview = async () => {
    if (!rating || comment.length < 5) { toast.error("Ingresa una calificación y comentario"); return; }
    try {
      await API.post(`/reviews`, { productId: product.id, rating, comment });
      toast.success("Reseña enviada. Será visible cuando sea revisada.");
      setRating(0); setComment(""); setAlreadyReviewed(true);
    } catch { toast.error("Error al enviar la reseña"); }
  };

  /* ── Loading ── */
  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <div className="w-10 h-10 border-4 border-supernova-400 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-400">Cargando producto...</p>
      </div>
    );
  }

  const price      = Number(product.price);
  const offerPrice = product.offerPrice ? Number(product.offerPrice) : null;
  const discount   = offerPrice ? Math.round((1 - offerPrice / price) * 100) : 0;
  const avgRating  = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length) : 0;
  const totalPrice = ((offerPrice ?? price) * qty).toLocaleString("es-CO");
  // companyId disponible aunque el backend no incluya la relación company completa
  const storeId    = product.company?.id ?? product.companyId;

  /* ── Cart CTA (shared between mobile fixed bar and desktop inline) ── */
  const CartCTA = ({ full = false }) => (
    <div className={`flex items-center gap-3 ${full ? "w-full" : ""}`}>
      <QtySelector qty={qty} setQty={setQty} />
      <motion.button
        whileTap={{ scale: 0.97 }}
        onClick={handleAddToCart}
        disabled={!canBuy || product.stock <= 0}
        className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm shadow-sm transition ${
          !canBuy || product.stock <= 0
            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
            : "bg-supernova-500 hover:bg-supernova-600 text-white"
        }`}
      >
        <ShoppingCart size={18} />
        {!canBuy ? "Sedes cerradas" : product.stock <= 0 ? "Sin stock" : (
          <>Agregar al carrito · <span className="font-black">${totalPrice}</span></>
        )}
      </motion.button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-36 lg:pb-10">

      {/* ── Desktop layout: two columns on lg+ ── */}
      <div className="lg:max-w-5xl lg:mx-auto lg:pt-6 lg:px-6">
        <div className="lg:flex lg:gap-6 lg:items-start">

          {/* ══ LEFT: Image column ══ */}
          <div className="lg:w-2/5 lg:flex-shrink-0 lg:sticky lg:top-20">

            {/* Image area */}
            {(() => {
              const images = product.images ?? [];
              const currentImg = images[imgIndex];
              const hasPrev = imgIndex > 0;
              const hasNext = imgIndex < images.length - 1;
              const goTo = (i) => setImgIndex(Math.max(0, Math.min(images.length - 1, i)));

              return (
                <div
                  className="relative bg-black lg:rounded-2xl lg:overflow-hidden"
                  onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
                  onTouchEnd={(e) => {
                    if (touchStartX.current === null) return;
                    const dx = e.changedTouches[0].clientX - touchStartX.current;
                    if (dx < -50 && hasNext) goTo(imgIndex + 1);
                    if (dx > 50 && hasPrev) goTo(imgIndex - 1);
                    touchStartX.current = null;
                  }}
                >
                  {/* Main image */}
                  <div
                    className="w-full h-72 sm:h-80 lg:h-96 flex items-center justify-center cursor-zoom-in"
                    onClick={() => currentImg && setZoomOpen(true)}
                  >
                    <AnimatePresence mode="wait">
                      {currentImg ? (
                        <motion.img
                          key={currentImg}
                          src={getImageUrl(currentImg)}
                          alt={product.title}
                          className="max-h-full max-w-full object-contain"
                          initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                          transition={{ duration: 0.2 }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-100">
                          <ImageOff size={48} className="text-gray-300" />
                        </div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Gradient overlay top */}
                  <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-transparent pointer-events-none" />

                  {/* Top actions */}
                  <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4 pointer-events-none">
                    <button onClick={(e) => { e.stopPropagation(); navigate(-1); }}
                      className="w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow pointer-events-auto">
                      <ArrowLeft size={18} className="text-gray-800" />
                    </button>
                    <div className="flex gap-2 pointer-events-auto">
                      {currentImg && (
                        <button onClick={(e) => { e.stopPropagation(); setZoomOpen(true); }}
                          className="w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow">
                          <ZoomIn size={16} className="text-gray-700" />
                        </button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); handleShare(); }}
                        className="w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow">
                        <Share2 size={16} className="text-gray-700" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); handleFavorite(); }}
                        className="w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow">
                        <Heart size={16} className={isFav ? "fill-red-500 text-red-500" : "text-gray-700"} />
                      </button>
                    </div>
                  </div>

                  {/* Discount badge */}
                  {discount > 0 && (
                    <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full shadow pointer-events-none">
                      -{discount}% OFF
                    </div>
                  )}

                  {/* Left / right arrows */}
                  {hasPrev && (
                    <button
                      onClick={(e) => { e.stopPropagation(); goTo(imgIndex - 1); }}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/40 hover:bg-black/60 rounded-full flex items-center justify-center text-white shadow transition"
                    >
                      <ChevronLeft size={20} />
                    </button>
                  )}
                  {hasNext && (
                    <button
                      onClick={(e) => { e.stopPropagation(); goTo(imgIndex + 1); }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/40 hover:bg-black/60 rounded-full flex items-center justify-center text-white shadow transition"
                    >
                      <ChevronRight size={20} />
                    </button>
                  )}

                  {/* Dot indicators / thumbnail strip */}
                  {images.length > 1 && (
                    <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 pointer-events-none">
                      {images.map((_, i) => (
                        <button
                          key={i}
                          onClick={(e) => { e.stopPropagation(); goTo(i); }}
                          className={`pointer-events-auto rounded-full transition-all ${
                            i === imgIndex ? "w-5 h-2 bg-white" : "w-2 h-2 bg-white/50"
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Thumbnail row */}
            {product.images?.length > 1 && (
              <div className="flex gap-2 px-4 pt-3 overflow-x-auto no-scrollbar">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setImgIndex(i)}
                    className={`w-14 h-14 flex-shrink-0 rounded-xl overflow-hidden border-2 transition ${
                      i === imgIndex ? "border-supernova-500 scale-105" : "border-gray-200"
                    }`}
                  >
                    <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Desktop: cart CTA below image */}
            <div className="hidden lg:block mt-4 bg-white rounded-2xl p-4 shadow-sm">
              <CartCTA full />
            </div>
          </div>

          {/* ── Zoom lightbox ── */}
          <AnimatePresence>
            {zoomOpen && product.images?.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center"
                onClick={() => setZoomOpen(false)}
                onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
                onTouchEnd={(e) => {
                  if (touchStartX.current === null) return;
                  const dx = e.changedTouches[0].clientX - touchStartX.current;
                  if (dx < -50 && imgIndex < product.images.length - 1) setImgIndex(i => i + 1);
                  if (dx > 50 && imgIndex > 0) setImgIndex(i => i - 1);
                  touchStartX.current = null;
                }}
              >
                <button
                  onClick={() => setZoomOpen(false)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white z-10"
                >
                  <X size={20} />
                </button>
                {imgIndex > 0 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setImgIndex(i => i - 1); }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white"
                  >
                    <ChevronLeft size={22} />
                  </button>
                )}
                {imgIndex < product.images.length - 1 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setImgIndex(i => i + 1); }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white"
                  >
                    <ChevronRight size={22} />
                  </button>
                )}
                <motion.img
                  key={imgIndex}
                  src={getImageUrl(product.images[imgIndex])}
                  alt={product.title}
                  className="max-w-full max-h-full object-contain px-16"
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  onClick={(e) => e.stopPropagation()}
                />
                {product.images.length > 1 && (
                  <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-2">
                    {product.images.map((_, i) => (
                      <button
                        key={i}
                        onClick={(e) => { e.stopPropagation(); setImgIndex(i); }}
                        className={`rounded-full transition-all ${i === imgIndex ? "w-5 h-2 bg-white" : "w-2 h-2 bg-white/40"}`}
                      />
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* ══ RIGHT: Info column ══ */}
          <div className="lg:flex-1 lg:min-w-0">

            {/* ── Info card ── */}
            <div className="bg-white rounded-t-3xl -mt-4 lg:mt-0 lg:rounded-2xl relative z-10 px-4 sm:px-5 pt-5 pb-4 shadow-sm">

              {/* Title + rating */}
              <div className="flex items-start justify-between gap-3">
                <h1 className="text-lg font-bold text-gray-900 leading-tight flex-1">{product.title}</h1>
                {reviews.length > 0 && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Star size={14} className="text-yellow-400 fill-yellow-400" />
                    <span className="text-sm font-bold text-gray-700">{avgRating.toFixed(1)}</span>
                    <span className="text-xs text-gray-400">({reviews.length})</span>
                  </div>
                )}
              </div>

              {/* Price */}
              <div className="flex items-center gap-3 mt-2">
                {offerPrice ? (
                  <>
                    <span className="text-2xl font-black text-supernova-600">
                      ${offerPrice.toLocaleString("es-CO")}
                    </span>
                    <span className="text-sm text-gray-400 line-through">
                      ${price.toLocaleString("es-CO")}
                    </span>
                  </>
                ) : (
                  <span className="text-2xl font-black text-supernova-600">
                    ${price.toLocaleString("es-CO")}
                  </span>
                )}
              </div>

              {/* Description */}
              {product.description && (
                <p className="text-sm text-gray-500 mt-2 leading-relaxed line-clamp-3">
                  {product.description}
                </p>
              )}

            </div>

            {/* ── Tienda + Sedes card ── */}
            {(storeId || sedesOrdenadas.length > 0) && (
              <div className="bg-white mt-2 shadow-sm lg:rounded-2xl overflow-hidden">

                {/* Company header */}
                {storeId && (
                  <div className="flex items-center justify-between gap-3 px-4 sm:px-5 pt-4 pb-3 border-b border-gray-100">
                    <div className="flex items-center gap-3 min-w-0">
                      {product.company?.logo ? (
                        <img
                          src={getImageUrl(product.company.logo)}
                          alt=""
                          className="w-10 h-10 rounded-xl object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-xl bg-supernova-100 flex items-center justify-center flex-shrink-0">
                          <Building2 size={20} className="text-supernova-500" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-bold text-gray-900 truncate">
                            {product.company?.name ?? "Tienda"}
                          </span>
                          {product.company?.verified && <BadgeCheck size={13} className="text-blue-500 flex-shrink-0" />}
                        </div>
                        <span className="text-xs text-gray-400">Tienda oficial</span>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate(`/company/${storeId}`)}
                      className="flex-shrink-0 flex items-center gap-1.5 px-3.5 py-2 bg-supernova-500 hover:bg-supernova-600 active:scale-95 text-white text-xs font-bold rounded-xl transition shadow-sm"
                    >
                      <Building2 size={13} />
                      Ir a la tienda
                    </button>
                  </div>
                )}

                {/* Sedes */}
                {sedesOrdenadas.length > 0 && (
                  <div className="px-4 sm:px-5 py-4">
                    <div className="flex flex-col gap-2">
                      {sedesOrdenadas.map((s) => {
                        const status = isSedeOpen(s);
                        const sLat = Number(s.lat ?? s.location?.lat);
                        const sLng = Number(s.lng ?? s.location?.lng);
                        const mapsUrl = sLat && sLng
                          ? `https://www.google.com/maps/dir/?api=1&destination=${sLat},${sLng}`
                          : null;
                        return (
                          <div key={s.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0 gap-2">
                            <div className="flex items-start gap-2 min-w-0">
                              <MapPin size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-gray-800">{s.name}</p>
                                {s.address && <p className="text-xs text-gray-400 truncate">{s.address}</p>}
                                {s.distance !== undefined && s.distance !== Infinity && (
                                  <p className="text-xs text-supernova-600 font-medium">{s.distance.toFixed(1)} km</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${status.open ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${status.open ? "bg-green-500" : "bg-red-400"}`} />
                                {status.open ? "Abierto" : "Cerrado"}
                              </div>
                              {mapsUrl && (
                                <a
                                  href={mapsUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-600 text-xs font-semibold rounded-lg transition"
                                >
                                  <MapPin size={11} />
                                  Cómo llegar
                                </a>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              </div>
            )}

            {/* ── Reviews section ── */}
            <div className="bg-white mt-2 shadow-sm lg:rounded-2xl">
              <button
                onClick={() => setShowReviews(!showReviews)}
                className="w-full flex items-center justify-between px-4 sm:px-5 py-4"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-gray-800">Reseñas</span>
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{reviews.length}</span>
                  {reviews.length > 0 && <StarRow value={Math.round(avgRating)} size={13} />}
                </div>
                <motion.div animate={{ rotate: showReviews ? 180 : 0 }} transition={{ duration: 0.2 }}>
                  <ChevronDown size={18} className="text-gray-400" />
                </motion.div>
              </button>

              <AnimatePresence>
                {showReviews && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 sm:px-5 pb-4 flex flex-col gap-3">
                      {reviews.length === 0 ? (
                        <p className="text-sm text-gray-400 py-2">Sin reseñas todavía.</p>
                      ) : (
                        reviews.map((r) => (
                          <div key={r.id} className="flex gap-3">
                            <div className="w-8 h-8 rounded-full bg-supernova-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-bold text-supernova-700">
                                {(r.user?.name || "U")[0].toUpperCase()}
                              </span>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold text-gray-700">{r.user?.name}</span>
                                <StarRow value={r.rating} size={11} />
                              </div>
                              <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{r.comment}</p>
                            </div>
                          </div>
                        ))
                      )}

                      {/* Write review */}
                      {!isLoggedIn ? (
                        <button onClick={() => navigate("/login")}
                          className="text-xs text-supernova-600 underline mt-1">
                          Inicia sesión para escribir una reseña
                        </button>
                      ) : alreadyReviewed ? (
                        <p className="text-xs text-green-600 bg-green-50 px-3 py-2 rounded-lg flex items-center gap-2">
                          ✓ Ya dejaste una reseña para este producto
                        </p>
                      ) : (
                        <div className={`mt-2 pt-3 border-t border-gray-100 ${!hasPurchased ? "opacity-50 pointer-events-none select-none" : ""}`}>
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-bold text-gray-700">Tu calificación</p>
                            {!hasPurchased && (
                              <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <ShoppingCart size={10} /> Debes comprar para calificar
                              </span>
                            )}
                          </div>
                          <StarPicker value={rating} onChange={hasPurchased ? setRating : () => {}} />
                          <textarea
                            value={comment}
                            onChange={(e) => hasPurchased && setComment(e.target.value)}
                            className="w-full mt-2 p-2.5 border border-gray-200 rounded-xl text-sm resize-none"
                            rows={2}
                            placeholder={hasPurchased ? "¿Qué te pareció este producto?" : "Compra el producto para dejar tu reseña"}
                            readOnly={!hasPurchased}
                          />
                          <button
                            onClick={hasPurchased ? handleSubmitReview : undefined}
                            disabled={!hasPurchased}
                            className="mt-2 w-full py-2 bg-supernova-500 text-white text-sm font-bold rounded-xl hover:bg-supernova-600 transition disabled:bg-gray-300 disabled:cursor-not-allowed">
                            {hasPurchased ? "Enviar reseña" : "Compra este producto para calificarlo"}
                          </button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

          </div>{/* end RIGHT column */}
        </div>
      </div>

      {/* ── Mobile/Tablet fixed bottom CTA (hidden on lg+) ── */}
      <div className="lg:hidden fixed bottom-16 left-0 right-0 z-40 bg-white border-t border-gray-100 shadow-2xl px-4 py-3">
        <div className="max-w-lg mx-auto">
          <CartCTA full />
        </div>
      </div>

      <MobileBottomBar />
    </div>
  );
}
